import React, { useState, useEffect, useCallback, useContext } from "react";
import IErrorDTO from "../../../../../models/DTO/IErrorDTO";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import IGroupItem from "../../../../../models/IGroupItem";
import ISupplierModel from "../../../../../models/ISupplierModel";
import IUserUI from "../../../../../models/IUserUI";
import OwButton from "../../../../shared/OwButton/OwButton";
import OwInputWrapper from "../../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import Typeahead from "../../../../shared/Typeahead/Typeahead";
import { DragDropContext, Droppable, Draggable, DropResult, ResponderProvided } from "@hello-pangea/dnd";
import "./ProductGroupDetail.scss";
import { Socket } from "socket.io-client";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import UserContext from "@contexts/UserContext";
import { SocketContext } from "@contexts/SocketContext";
import UiContext from "@contexts/UiContext";
import SupplierContext from "@contexts/SupplierContext";
import IUiSocketSetup from "@models/IUiSocketSetup";
import IGroupItemUI from "@models/IGroupItemUI";
import ICatalog_Item from "@models/ICatalog_Item";
import { get, isNil, sortBy } from "lodash-es";
import _ from "lodash";

type Props = {
    catalogGuid: string;
    groupCatalogItems: IGroupItem[];
    updateGroupItemsFn: (groupItems: IGroupItem[]) => void;
};

const ProductGroupDetail: React.FC<Props> = ({ catalogGuid, groupCatalogItems: initialGroupCatalogItems, updateGroupItemsFn }) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [groupCatalogItems, setGroupCatalogItems] = useState<IGroupItemUI[]>([]);
    const [typeaheadItems, setTypeaheadItems] = useState<ICatalog_Item[]>([]);
    const [typeaheadItem, setTypeaheadItem] = useState<ICatalog_Item>();
    const [clearTypeahead, setClearTypeahead] = useState<boolean>();
    const [disableAdd, setDisableAdd] = useState<boolean>(false);
    const [nextSequenceNumber, setNextSequenceNumber] = useState<number>(1);

    const onTypeaheadCatalogItem = useCallback((data: any) => {
        if (!_.isNil(data)) {
            setTypeaheadItems(data.catalog_items);
        }
    }, []);

    const doCatalogItemLookup = useCallback(
        (name: string) => {
            const data = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid: catalogGuid,
                name: name,
            };

            if (name.length) {
                socket.emit("typeaheadCatalogItem", data);
            }
        },
        [userModel.guid, userModel.token, supplierModel.guid, catalogGuid, socket]
    );

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeaheadCatalogItem }];

    useEffect(() => {
        sockets.forEach((detail) => {
            socket.on(detail.endpoint, detail.fn);
        });

        const processedGroupCatalogItems = sortBy(initialGroupCatalogItems, ["group_sequence", "asc"]).map(
            (item: IGroupItem, index: number) => {
                const newItem = { ...item } as IGroupItemUI;

                newItem.originalTitle = item.group_title || "";
                newItem.editMode = false;
                newItem.deleteFlag = false;
                newItem.catalog_item_id = newItem.id;

                if (_.isNil(item.group_sequence)) {
                    item.group_sequence = index + 1;
                }

                newItem.index = index;

                return newItem;
            }
        );

        let newDisableAdd = false;
        let newNextSequenceNumber = 1;

        if (processedGroupCatalogItems.length >= 13) {
            newDisableAdd = true;
            newNextSequenceNumber = 13;
        } else {
            newNextSequenceNumber = processedGroupCatalogItems.length + 1;
        }

        setGroupCatalogItems(processedGroupCatalogItems);
        setDisableAdd(newDisableAdd);
        setNextSequenceNumber(newNextSequenceNumber);

        return () => {
            sockets.forEach((detail) => {
                socket.off(detail.endpoint, detail.fn);
            });
        };
    }, []);

    const addItem = useCallback(() => {
        if (!_.isNil(typeaheadItem)) {
            const newItem = { ...typeaheadItem } as IGroupItemUI;
            newItem.editMode = true;
            newItem.group_title = "";
            newItem.group_sequence = 1;
            newItem.originalTitle = "";
            newItem.catalog_item_id = newItem.id;

            const newGroupCatalogItems = [...groupCatalogItems, newItem];

            setGroupCatalogItems(newGroupCatalogItems);

            if (newGroupCatalogItems.length >= 13) {
                setDisableAdd(true);
            }

            updateGroupItemsFn(newGroupCatalogItems);

            console.log("addItem");
        }
    }, [typeaheadItem, groupCatalogItems, updateGroupItemsFn]);

    const toggleEditMode = (index: number, resetFlag?: boolean) => {
        const _groupCatalogItems = [...groupCatalogItems];
        _groupCatalogItems[index].editMode = !_groupCatalogItems[index].editMode;

        if (resetFlag) {
            _groupCatalogItems[index].group_title = _groupCatalogItems[index].originalTitle;
        }

        setGroupCatalogItems(_groupCatalogItems);
    };

    const isSelectableItem = useCallback((item: any) => {
        let isSelectable = true;

        const qty_tracked = !isNil(item.qty_on_hand);
        let is_in_stock;

        if (!isNil(qty_tracked)) {
            is_in_stock = get(item, "qty_on_hand", 0);
        }

        if (qty_tracked && !is_in_stock) {
            const allow_backorder = _.get(item, "allow_backorder", false);

            if (!allow_backorder) {
                isSelectable = false;
            }
        }

        console.log("isSelectable", isSelectable);

        return isSelectable;
    }, []);

    const clearTypeaheadCallback = useCallback(() => {
        setClearTypeahead(false);
        setTypeaheadItem(undefined);
    }, []);

    const selectItem = useCallback((selectedTypeaheadItem: any) => {
        setTypeaheadItem(selectedTypeaheadItem);
    }, []);

    const removeGroupItem = (index: number, undo?: boolean) => {
        const _groupCatalogItems = [...groupCatalogItems];
        const item = _groupCatalogItems[index];

        if (item.group_parent_catalog_item_id) {
            if (item.deleteFlag) {
                item.deleteFlag = false;
            } else {
                item.deleteFlag = true;
            }
        } else {
            _groupCatalogItems.splice(index, 1);
        }

        updateGroupItemsFn(_groupCatalogItems);
        setGroupCatalogItems(_groupCatalogItems);
    };

    const handleChange = useCallback(
        (e: any, index: number) => {
            const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
            const name = e.target.name;

            setGroupCatalogItems((prevItems) => {
                const newItems = [...prevItems];
                newItems[index][name] = value;
                updateGroupItemsFn(newItems);
                return newItems;
            });
        },
        [updateGroupItemsFn]
    );

    const onDragEnd = useCallback(
        (result: DropResult, provided: ResponderProvided) => {
            console.log("onDragEnd", result);
            const sourceIndex = result.source.index;
            const destIndex = result.destination ? result.destination.index : undefined;

            if (!_.isNil(destIndex) && sourceIndex !== destIndex) {
                setGroupCatalogItems((prevItems) => {
                    const newItems = [...prevItems];
                    const sourceItem = newItems[sourceIndex];

                    sourceItem.index = destIndex;
                    sourceItem.group_sequence = destIndex + 1;

                    console.log("sourceItem", sourceItem);

                    newItems.splice(sourceIndex, 1);
                    newItems.splice(destIndex, 0, sourceItem);

                    let index = 0;

                    for (const item of newItems) {
                        item.index = index;
                        item.group_sequence = index + 1;
                        ++index;
                    }

                    updateGroupItemsFn(newItems);
                    return newItems;
                });
            }
        },
        [updateGroupItemsFn]
    );

    return (
        <div className="ProductGroupDetail">
            <div className="bd-callout">
                <OwLabel title={"product_group"} description={"Product Group"} leftIconClass={"fad fa-sitemap"} />
            </div>
            <div className={"d-flex"}>
                <OwLabel title={"product_group_items"} description={"Product Group Items"} />
                <OwLabel title={"max_13_items"} className={"ms-auto text-warning"} />
            </div>
            <div className={"d-flex flex-row"}>
                <Typeahead
                    outerWrapperClassName={"w-100"}
                    typeaheadItems={typeaheadItems}
                    isSelectableFn={isSelectableItem}
                    typeaheadApiFn={doCatalogItemLookup}
                    titleProp="name"
                    subTitleProp="description"
                    maxResults={8}
                    placeholder="Search Catalog Items"
                    selectedItem={typeaheadItem}
                    clearTypeaheadCallback={clearTypeaheadCallback}
                    clearTypeahead={clearTypeahead}
                    selectedItemFn={selectItem}
                />
                <OwButton title={"add"} fn={addItem} disabled={disableAdd} />
            </div>
            <div>
                <div className={"w-100 p-2 d-flex"}>
                    <div className={"w-20"}></div>
                    <div className={"w-50"}>
                        <OwLabel title={"sku"} isBold={true} noBottomMargin={true} />
                    </div>
                    <div className={"w-30"}>
                        <OwLabel title={"title"} isBold={true} noBottomMargin={true} />
                    </div>
                </div>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId={"productGroupItems"}>
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                {groupCatalogItems.map((item: any, index: number) => {
                                    if (item) {
                                        return (
                                            <Draggable draggableId={item.guid + Date.now()} index={index} key={index}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        key={index}
                                                        className={"w-100 p-2 d-flex form-group h-2r product-group-row"}>
                                                        <div className={"w-25"}>
                                                            <div
                                                                className={"d-inline-flex text-muted me-3 grab"}
                                                                {...provided.dragHandleProps}>
                                                                <i className="far fa-ellipsis-v me-1"></i>
                                                                <i className="far fa-ellipsis-v"></i>
                                                            </div>
                                                            <div className="d-inline-flex">
                                                                <OwDeleteEditActions
                                                                    isEditMode={item.editMode}
                                                                    onTrashClickFn={() => {
                                                                        removeGroupItem(index, item.deleteFlag);
                                                                    }}
                                                                    onEditClickFn={() => {
                                                                        toggleEditMode(index);
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* <div className={item.editMode ? "d-none" : "d-inline-flex"}>
                                                                    <i
                                                                        className={
                                                                            "fad text-danger me-2 pointer " +
                                                                            (item && item.deleteFlag ? "fa-trash-undo" : "fa-trash-alt")
                                                                        }
                                                                        onClick={() => {
                                                                            removeGroupItem(index, item.deleteFlag);
                                                                        }}></i>
                                                                    <i
                                                                        className={"fad fa-pencil text-primary pointer"}
                                                                        onClick={() => {
                                                                            toggleEditMode(index);
                                                                        }}></i>
                                                                </div> */}
                                                            <div className={item.editMode ? "d-inline-flex" : "d-none"}>
                                                                <i
                                                                    className={"fad fa-times text-warning me-2 pointer"}
                                                                    onClick={() => {
                                                                        toggleEditMode(index, true);
                                                                    }}></i>
                                                                <i
                                                                    className={"fad fa-check text-success pointer"}
                                                                    onClick={() => {
                                                                        toggleEditMode(index);
                                                                    }}></i>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={
                                                                "w-50 " + (item && item.deleteFlag ? "text-strikethrough text-muted" : "")
                                                            }>
                                                            {item ? item.sku : ""}
                                                        </div>
                                                        <div className={"w-30 text-center"}>
                                                            <div className={item.editMode ? "" : "d-none"}>
                                                                <OwInputWrapper
                                                                    name={"group_title"}
                                                                    value={item.group_title}
                                                                    fn={(e: any) => {
                                                                        handleChange(e, index);
                                                                    }}
                                                                    maxLength={3}
                                                                    placeholder={"max_length_3"}
                                                                />
                                                            </div>
                                                            <div className={item.editMode ? "d-none" : ""}>{item.group_title}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    }
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
};

export default ProductGroupDetail;
